import React, { useState, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';

interface OnboardingTourProps {
  isRunning: boolean;
  onClose: () => void;
  autoStart?: boolean;
}

const tourSteps: Step[] = [
  {
    target: '.dashboard-welcome',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">Welcome to RenovirAI! 🏠</h3>
        <p>Your AI-powered renovation assistant that transforms how you visualize, plan, and execute home renovation projects.</p>
        <p className="mt-2 text-sm text-gray-600">Let's take a quick tour to show you what you can do!</p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '.token-balance',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">Your Token Balance 💰</h3>
        <p>RenovirAI uses a simple token system. Each AI generation costs 6-10 tokens.</p>
        <p className="mt-2 text-sm text-gray-600">You can purchase tokens anytime to power your AI renovations!</p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '.generation-card-moodboard',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">🎨 Moodboard Generator</h3>
        <p>Create comprehensive design boards with multiple room variations.</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li>Choose from 20+ room styles</li>
          <li>Mix and match different aesthetics</li>
          <li>Perfect for design planning and client presentations</li>
        </ul>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '.generation-card-ai-touchup',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">🖌️ Edit Tool</h3>
        <p>Your precision AI editing suite with three powerful modes:</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li><strong>Erase:</strong> Remove unwanted objects from photos</li>
          <li><strong>Change:</strong> Transform objects into something else</li>
          <li><strong>Clean Selected:</strong> AI finds and removes specific furniture</li>
        </ul>
        <p className="mt-2 text-sm text-gray-600">Draw masks or describe what you want changed!</p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '.generation-card-ai-cleanup',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">🚀 Room Restyle</h3>
        <p>Transform entire rooms with AI-powered style changes.</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li>Choose room type and desired style</li>
          <li>AI considers room context for realistic results</li>
          <li>Perfect for renovation planning and staging</li>
        </ul>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '.generation-card-ai-remove-furniture',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">🪑 Remove Furniture</h3>
        <p>One-click furniture removal for staging and renovation planning.</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li>AI automatically detects all furniture</li>
          <li>No manual masking required</li>
          <li>Perfect for creating empty rooms for virtual staging</li>
        </ul>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '.generation-card-your-gallery',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">📱 Your Gallery</h3>
        <p>Manage all your uploaded images and AI generations in one place.</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li>View all past AI generations</li>
          <li>Reuse images across different tools</li>
          <li>Organize your renovation projects</li>
        </ul>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '.all-generations-viewer',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">📋 Recent Generations</h3>
        <p>See your latest AI creations right here on the dashboard.</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li>View before/after comparisons</li>
          <li>Click any image to see full details</li>
          <li>Download your favorite results</li>
          <li>Track all your AI generations in one place</li>
        </ul>
        <p className="mt-2 text-sm text-blue-600 font-medium">💡 Tip: Visit Your Gallery to see everything organized by project!</p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '.dashboard-welcome',
    content: (
      <div>
        <h3 className="text-lg font-semibold mb-2">Ready to Start Creating! 🚀</h3>
        <p>You now know the basics of RenovirAI. Here are some tips to get started:</p>
        <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
          <li>Start with any tool that interests you</li>
          <li>Upload your own photos or use existing ones</li>
          <li>Experiment with different styles and operations</li>
          <li>Check your token balance before generating</li>
        </ul>
        <p className="mt-3 font-medium text-blue-600">Happy renovating! ✨</p>
      </div>
    ),
    placement: 'center',
  },
];

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ isRunning, onClose, autoStart = false }) => {
  const [stepIndex, setStepIndex] = useState(0);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, type, index } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onClose();
    } else if (type === 'step:after') {
      setStepIndex(index + 1);
    }
  }, [onClose]);

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      run={isRunning}
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={tourSteps}
      stepIndex={stepIndex}
      styles={{
        options: {
          primaryColor: '#3B82F6',
          backgroundColor: '#ffffff',
          textColor: '#374151',
          overlayColor: 'rgba(0, 0, 0, 0.4)',
          arrowColor: '#ffffff',
          zIndex: 1000,
        },
        tooltip: {
          borderRadius: 12,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          fontSize: 14,
          padding: 20,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          color: '#1F2937',
          fontSize: 16,
          fontWeight: 600,
        },
        tooltipContent: {
          padding: '10px 0 0 0',
        },
        buttonNext: {
          backgroundColor: '#3B82F6',
          borderRadius: 6,
          border: 'none',
          color: '#ffffff',
          fontSize: 14,
          fontWeight: 500,
          padding: '8px 16px',
        },
        buttonBack: {
          color: '#6B7280',
          marginRight: 10,
          backgroundColor: 'transparent',
          border: '1px solid #D1D5DB',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 500,
          padding: '8px 16px',
        },
        buttonSkip: {
          color: '#6B7280',
          fontSize: 14,
          fontWeight: 500,
        },
        spotlight: {
          borderRadius: 8,
        },
      }}
      locale={{
        back: 'Previous',
        close: 'Close',
        last: 'Finish Tour',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
};
